import React, {useEffect, useState} from 'react';
import {Link, useHistory, useParams} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import {Button, Card, Col, Form, Row} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faSave, faTimes} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import {toast} from "react-toastify";
import {
    createAccount,
    getAccountDetails,
    getAccountTypeList,
    updateMonthlyTarget
} from "../Controller/AccountController";
import { withNamespaces } from 'react-i18next';
import Utility from '../../../../GlobalModules/Utility/Utility';

// Status options will be generated dynamically using translations

// Type options will be generated dynamically using translations

// POS default options will be generated dynamically using translations

const defaultValues = {
    name: null,
    type: null,
    status: null,
    initialBalance: null,
    posDefaultAccount: null,
    description: null
}

// getAccountTypeList

const AccountForm = ({ isEdit = false, ...props }) => {
    const { t, i18n } = props;
    const lng = i18n.language;
    const [accountTypeOptions, setAccountTypeOptions] = useState([]);

    // Dynamic options using translations
    const STATUS_OPTIONS = [
        { label: t('accountForm.statusOptions.active'), value: 1 },
        { label: t('accountForm.statusOptions.inactive'), value: 0 }
    ];

    const TYPE_OPTIONS = [
        { label: t('accountForm.typeOptions.basic'), value: 1 },
        { label: t('accountForm.typeOptions.asset'), value: 2 },
        { label: t('accountForm.typeOptions.liability'), value: 3 },
        { label: t('accountForm.typeOptions.equity'), value: 4 },
        { label: t('accountForm.typeOptions.revenue'), value: 5 },
        { label: t('accountForm.typeOptions.expense'), value: 6 }
    ];

    const POST_DEFAULT_ACCOUNT_OPTIONS = [
        { label: t('accountForm.posDefaultOptions.yes'), value: true },
        { label: t('accountForm.posDefaultOptions.no'), value: false }
    ];

    const history = useHistory();
    const { slack } = useParams();

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset
    } = useForm({ defaultValues });

    // ---- submit ----
    const onSubmit = async (data) => {
        try {
            const formattedData = {
                account_name: data.name,
                account_type: data.type.value,
                initial_balance: +data.initialBalance,
                description: data.description,
                pos_default: data.posDefaultAccount.value,
                status: data.status.value,
            }

            if(!isEdit){
                formattedData["store_id"] = localStorage.getItem('store_id')
            }
            
            if (isEdit) {
                const response = await updateMonthlyTarget(slack, formattedData);
                if (response.success) {
                    // toast.success(response.message || t('accountForm.accountUpdatedSuccess'));
                    Utility.toastNotifications(
                        response.message || t('accountForm.accountUpdatedSuccess'),
                        'Success',
                        'success'
                    )
                    history.push(`/${lng}/business/accounts/list`);
                } else {
                    // toast.error(response.message || t('accountForm.updateFailed'));
                    Utility.toastNotifications(
                        response.message || t('accountForm.updateFailed'),
                        'Error',
                        'error'
                    )
                }
            } else {
                const response = await createAccount(formattedData);
                if (response.success) {
                    // toast.success(response.message || t('accountForm.accountCreatedSuccess'));
                    Utility.toastNotifications(
                        response.message || t('accountForm.accountCreatedSuccess'),
                        'Success',
                        'success'
                    )
                    history.push(`/${lng}/business/accounts/list`);
                } else {
                    // toast.error(response.message || t('accountForm.createFailed'));
                    Utility.toastNotifications(
                        response.message || t('accountForm.createFailed'),
                        'Error',
                        'error'
                    )
                }
            }
        } catch (e) {
            console.error('Error saving account:', e);
            // toast.error(t('accountForm.saveError'));
            // Utility.toastNotifications(
            //     t('accountForm.saveError'),
            //     'Error',
            //     'error'
            // )
        }
    };

    // ---------- Validation helpers ----------
    const numericWithDecimalsMsg = (fieldLabel) =>
        `${t('accountForm.numericValidation', { fieldLabel })}`;

    const validateNumericOrEmpty = (v, fieldLabel) =>
        v === '' || v === null || v === undefined || isFinite(Number(v))
            ? true
            : numericWithDecimalsMsg(fieldLabel);

    // helpers
    const findOpt = (opts, v) => opts.find(o => o.value === v);
// sometimes APIs return strings: coerce if needed
    const findOptNum = (opts, v) => opts.find(o => o.value === Number(v));

    // 2) accept options so we don't depend on just-updated state
    const fetchAccount = async (optionsArg) => {
        try {
            const response = await getAccountDetails(slack);
            if (response.success && response.data) {
                const account = response.data;
                const options = optionsArg?.length ? optionsArg : accountTypeOptions;

                reset({
                    name: account.label ?? '',
                    type: findOptNum(options, account.account_type),
                    status: findOptNum(STATUS_OPTIONS, account.status),
                    initialBalance: account.initial_balance ?? '',
                    posDefaultAccount: findOpt(
                        POST_DEFAULT_ACCOUNT_OPTIONS,
                        account.pos_default === 1 || account.pos_default === true
                    ),
                    description: account.description ?? ''
                });
            } else {
                toast.error(t('accountForm.accountNotFound'));
            }
        } catch (error) {
            console.error('Error fetching account:', error);
            toast.error(t('accountForm.fetchError'));
        }
    };

    // 1) return options from fetchAccountTypes
    const fetchAccountTypes = async () => {
        try {
            const response = await getAccountTypeList();
            if (response.success && response.data) {
                const options = response.data.map(el => ({ label: el.label, value: el.id }));
                setAccountTypeOptions(options);
                return options; // <-- important
            } else {
                toast.error('Account types not found');
                return [];
            }
        } catch (error) {
            console.error('Error fetching account types:', error);
            toast.error('Failed to fetch account types');
            return [];
        }
    };

    // 3) single orchestrating effect — no accountTypeOptions in deps
    useEffect(() => {
        (async () => {
            const options = await fetchAccountTypes();

            if (isEdit && slack) {
                await fetchAccount(options); // only once
            }
        })();
        // only re-run if edit mode or id changes
    }, [isEdit, slack, reset]);


    return <Card className="supplier-form-card">
        <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{isEdit ? t('accountForm.editAccount') : t('accountForm.addAccount')}</h5>
                <div>
                    <Link
                        to={`/${lng}/business/accounts/list`}
                        className="btn btn-secondary me-2"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                        {t('accountForm.back')}
                    </Link>
                </div>
            </div>
        </Card.Header>

        <Card.Body>
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Row className={'mb-4'}>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>{t('accountForm.accountName')}</Form.Label>
                            <Controller
                                control={control}
                                name="name"
                                rules={{required: t('accountForm.accountNameRequired')}}
                                render={({field}) => (
                                    <Form.Control rows={5} placeholder={t('accountForm.accountNamePlaceholder')} {...field} />
                                )}
                            />
                            {errors.name && (
                                <div className="invalid-feedback d-block">
                                    {errors.name.message}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>{t('accountForm.accountType')}</Form.Label>
                            <Controller
                                control={control}
                                name="type"
                                rules={{required: t('accountForm.accountTypeRequired')}}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        options={accountTypeOptions}
                                        isClearable={false}
                                        isSearchable={false}
                                        value={field.value}
                                        onChange={(opt) => field.onChange(opt)}
                                        placeholder={t('accountForm.accountTypePlaceholder')}
                                    />
                                )}
                            />
                            {errors.type && (
                                <div className="invalid-feedback d-block">{errors.type.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>{t('accountForm.status')}</Form.Label>
                            <Controller
                                control={control}
                                name="status"
                                rules={{required: t('accountForm.statusRequired')}}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        options={STATUS_OPTIONS}
                                        isClearable={false}
                                        isSearchable={false}
                                        value={field.value}
                                        onChange={(opt) => field.onChange(opt)}
                                        placeholder={t('accountForm.statusPlaceholder')}
                                    />
                                )}
                            />
                            {errors.status && (
                                <div className="invalid-feedback d-block">{errors.status.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
                <Row className={'mb-4'}>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>{t('accountForm.initialBalance')}</Form.Label>
                            <Controller
                                control={control}
                                name={'initialBalance'}
                                rules={{
                                    // validate: (v) => validateNumericOrEmpty(v, t('accountForm.initialBalance')),
                                    // required: t('accountForm.initialBalanceRequired')
                                    validate: (v) => {
                                        if (v === '' || v === null || v === undefined) return true;
                                        const num = Number(v);
                                        if (isNaN(num)) return 'The Initial Balance field must be numeric and may contain decimal points';
                                        if (num < 0) return 'Initial Balance cannot be negative';
                                        return true;
                                    },
                                    required: 'The Initial Balance field must be numeric and may contain decimal points'
                                }}
                                render={({field}) => (
                                    <Form.Control
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="0.01"
                                        value={field.value ?? ''}
                                        placeholder={t('accountForm.initialBalancePlaceholder')}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                )}
                            />
                            {errors.initialBalance && (
                                <div className="invalid-feedback d-block">
                                    {errors.initialBalance.message}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>{t('accountForm.posDefaultAccount')}</Form.Label>
                            <Controller
                                control={control}
                                name="posDefaultAccount"
                                rules={{required: t('accountForm.posDefaultAccountRequired')}}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        options={POST_DEFAULT_ACCOUNT_OPTIONS}
                                        isClearable={false}
                                        isSearchable={false}
                                        value={field.value}
                                        onChange={(opt) => field.onChange(opt)}
                                        placeholder={t('accountForm.posDefaultAccountPlaceholder')}
                                    />
                                )}
                            />
                            {errors.posDefaultAccount && (
                                <div className="invalid-feedback d-block">{errors.posDefaultAccount.message}</div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>{t('accountForm.description')}</Form.Label>
                            <Controller
                                control={control}
                                name="description"
                                render={({ field }) => (
                                    <Form.Control as="textarea" rows={5} placeholder={t('accountForm.descriptionPlaceholder')} {...field} />
                                )}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <div className="mt-4">
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                  <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                  ></span>
                                {t('accountForm.saving')}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} className="me-1"/>
                                {t('accountForm.save')}
                            </>
                        )}
                    </Button>
                    <Button variant="secondary" type="button" onClick={() => history.goBack()} className="ms-2">
                        <FontAwesomeIcon icon={faTimes} className="me-1"/>
                        {t('accountForm.cancel')}
                    </Button>
                </div>
            </Form>
        </Card.Body>
    </Card>
}

export default withNamespaces()(AccountForm)